import * as XLSX from 'xlsx'
import { SentryIssue } from '@/types/sentryIssue.type'
import { ExportIssue } from '@/hooks/react-query/useExportIssues'
import { CloudWatchTableRow } from '@/types/cloudWatch.type'

/**
 * Export issues to CSV format
 */
export const exportToCSV = (issues: SentryIssue[], filename: string) => {
  // Define CSV headers
  const headers = [
    'Issue ID',
    'Title',
    'Subtitle',
    'Project',
    'Environment',
    'Status',
    'Level',
    'Priority',
    'Last Seen',
    'Age',
    'Events',
    'Users',
    'Workstream',
    'Unhandled',
  ]

  // Convert issues to CSV rows
  const rows = issues.map((issue) => [
    issue.id,
    issue.title || '',
    issue.subtitle || '',
    issue.project || '',
    issue.enrichedEnvironment || '',
    issue.status || '',
    issue.level || '',
    issue.priority || '',
    issue.lastSeen || '',
    issue.age || '',
    issue.totalEvents?.toString() || '0',
    issue.affectedUsers?.toString() || '0',
    issue.workstream || '',
    issue.isUnhandled ? 'Yes' : 'No',
  ])

  // Combine headers and rows
  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n')

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Export issues to XLSX format
 */
export const exportToXLSX = (issues: SentryIssue[], filename: string) => {
  // Prepare data for XLSX
  const data = issues.map((issue) => ({
    'Issue ID': issue.id,
    Title: issue.title || '',
    Subtitle: issue.subtitle || '',
    Project: issue.project || '',
    Environment: issue.enrichedEnvironment || '',
    Status: issue.status || '',
    Level: issue.level || '',
    Priority: issue.priority || '',
    'Last Seen': issue.lastSeen || '',
    Age: issue.age || '',
    Events: issue.totalEvents || 0,
    Users: issue.affectedUsers || 0,
    Workstream: issue.workstream || '',
    Unhandled: issue.isUnhandled ? 'Yes' : 'No',
    Trend: issue.trend || '',
  }))

  // Create workbook and worksheet
  const worksheet = XLSX.utils.json_to_sheet(data)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Issues')

  // Set column widths
  const columnWidths = [
    { wch: 15 }, // Issue ID
    { wch: 50 }, // Title
    { wch: 60 }, // Subtitle
    { wch: 20 }, // Project
    { wch: 15 }, // Environment
    { wch: 12 }, // Status
    { wch: 10 }, // Level
    { wch: 10 }, // Priority
    { wch: 15 }, // Last Seen
    { wch: 10 }, // Age
    { wch: 10 }, // Events
    { wch: 10 }, // Users
    { wch: 15 }, // Workstream
    { wch: 10 }, // Unhandled
    { wch: 12 }, // Trend
  ]
  worksheet['!cols'] = columnWidths

  // Write file
  XLSX.writeFile(workbook, filename)
}

/**
 * Generate filename based on workstream and date range
 */
export const generateExportFilename = (
  format: 'csv' | 'xlsx',
  workstream: string,
  startDate: Date | null,
  endDate: Date | null
): string => {
  const workstreamName = workstream === 'all' ? 'all-workstreams' : workstream
  const dateStr =
    startDate && endDate
      ? `${startDate.toISOString().split('T')[0]}_to_${endDate.toISOString().split('T')[0]}`
      : new Date().toISOString().split('T')[0]

  return `sentry-issues-${workstreamName}-${dateStr}.${format}`
}

/**
 * Generate date range string for display
 */
export const generateDateRangeString = (startDate: Date | null, endDate: Date | null): string => {
  if (!startDate || !endDate) return 'N/A'
  return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
}

/**
 * Export API response data to CSV format
 * This function is specifically for data from the /api/sentry/issues/export endpoint
 */
export const exportApiDataToCSV = (issues: ExportIssue[], filename: string) => {
  // Define CSV headers matching API response structure
  const headers = ['Issue', 'Issue ID', 'Short ID', 'Environment', 'Last Seen', 'Age', 'Events', 'Users', 'Priority']

  // Convert issues to CSV rows
  const rows = issues.map((issue) => [
    issue.Issue,
    issue['Issue ID'],
    issue['Short ID'],
    issue.Environment,
    issue['Last Seen'],
    issue.Age,
    issue.Events.toString(),
    issue.Users.toString(),
    issue.Priority,
  ])

  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      row
        .map((cell) => {
          // Escape cells containing commas, quotes, or newlines
          if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))) {
            return `"${cell.replace(/"/g, '""')}"`
          }
          return cell
        })
        .join(',')
    ),
  ].join('\n')

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Export API response data to XLSX format
 * This function is specifically for data from the /api/sentry/issues/export endpoint
 */
export const exportApiDataToXLSX = (issues: ExportIssue[], filename: string) => {
  // Prepare data for XLSX - data is already in the correct format from API
  const data = issues.map((issue) => ({
    Issue: issue.Issue,
    'Issue ID': issue['Issue ID'],
    'Short ID': issue['Short ID'],
    Environment: issue.Environment,
    'Last Seen': issue['Last Seen'],
    Age: issue.Age,
    Events: issue.Events,
    Users: issue.Users,
    Priority: issue.Priority,
  }))

  // Create workbook and worksheet
  const worksheet = XLSX.utils.json_to_sheet(data)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Issues')

  // Set column widths
  const columnWidths = [
    { wch: 60 }, // Issue
    { wch: 15 }, // Issue ID
    { wch: 20 }, // Short ID
    { wch: 15 }, // Environment
    { wch: 25 }, // Last Seen
    { wch: 15 }, // Age
    { wch: 10 }, // Events
    { wch: 10 }, // Users
    { wch: 10 }, // Priority
  ]
  worksheet['!cols'] = columnWidths

  // Write file
  XLSX.writeFile(workbook, filename)
}

/**
 * Export CloudWatch logs to CSV format
 */
export const exportCloudWatchToCSV = (logs: CloudWatchTableRow[], filename: string) => {
  // Define CSV headers
  const headers = ['Error Message', 'Error Code', 'Lambda Function', 'Log Group', 'Stack Name', 'Region', 'Timestamp']

  // Convert logs to CSV rows
  const rows = logs.map((log) => [
    log.error_message,
    log.error_code,
    log.lambda_function,
    log.log_group,
    log.stack_name,
    log.region,
    log.timestamp,
  ])

  // Combine headers and rows
  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n')

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Export CloudWatch logs to XLSX format
 */
export const exportCloudWatchToXLSX = (logs: CloudWatchTableRow[], filename: string) => {
  // Prepare data for XLSX
  const data = logs.map((log) => ({
    'Error Message': log.error_message,
    'Error Code': log.error_code,
    'Lambda Function': log.lambda_function,
    'Log Group': log.log_group,
    'Stack Name': log.stack_name,
    Region: log.region,
    Timestamp: log.timestamp,
  }))

  // Create workbook and worksheet
  const worksheet = XLSX.utils.json_to_sheet(data)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'CloudWatch Logs')

  // Set column widths
  const columnWidths = [
    { wch: 60 }, // Error Message
    { wch: 12 }, // Error Code
    { wch: 30 }, // Lambda Function
    { wch: 40 }, // Log Group
    { wch: 40 }, // Stack Name
    { wch: 15 }, // Region
    { wch: 25 }, // Timestamp
  ]
  worksheet['!cols'] = columnWidths

  // Write file
  XLSX.writeFile(workbook, filename)
}

/**
 * Generate filename for CloudWatch exports
 */
export const generateCloudWatchExportFilename = (
  format: 'csv' | 'xlsx',
  workstream: string,
  startDate: string,
  endDate: string
): string => {
  const workstreamName = workstream === 'all' ? 'all-workstreams' : workstream
  const dateStr = `${startDate}_to_${endDate}`

  return `cloudwatch-logs-${workstreamName}-${dateStr}.${format}`
}
